import React, { useState, useEffect } from "react";
import ParentPanel from "../panel/ParentPanel";
import "../styles/SeeResults.css";
import api from "../../../api/api";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import {
  FaClipboardList,
  FaBookOpen,
  FaTasks,
  FaMedal,
  FaPencilRuler,
  FaProjectDiagram,
  FaFileAlt,
  FaFlask,
  FaTrophy,
  FaDownload,
} from "react-icons/fa";

const SeeResults = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeExam, setActiveExam] = useState(null); 
  const [selectedYear, setSelectedYear] = useState("");
  const [availableYears, setAvailableYears] = useState([]);
  const [results, setResults] = useState([]);
  const [previousYearResults, setPreviousYearResults] = useState([]); // NEW
  const [activePrevExam, setActivePrevExam] = useState(null); // NEW
  const [notifications, setNotifications] = useState([]);
  const [queries, setQueries] = useState([]);
  const [queryMessages, setQueryMessages] = useState({});

  const token = localStorage.getItem("token"); // JWT from login

  const exams = [
    { name: "Unit Test 1", icon: <FaClipboardList />, color: "sr-orange" },
    { name: "Unit Test 2", icon: <FaClipboardList />, color: "sr-red" },
    { name: "Half-Yearly Exam", icon: <FaBookOpen />, color: "sr-green" },
    { name: "Pre-Board Exam", icon: <FaTasks />, color: "sr-pink" },
    { name: "Annual Exam", icon: <FaMedal />, color: "sr-darkred" },
    { name: "Class Test", icon: <FaPencilRuler />, color: "sr-orange" },
    { name: "Assignment / Project", icon: <FaProjectDiagram />, color: "sr-green" },
    { name: "Mid-Term Test", icon: <FaFileAlt />, color: "sr-blue" },
    { name: "Practical Exam", icon: <FaFlask />, color: "sr-purple" },
    { name: "Final Term Exam", icon: <FaTrophy />, color: "sr-deeporange" },
  ];

  // --- Fetch available years from backend ---
  useEffect(() => {
    if (!token) return;
    api.get("/api/students/available-years", {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => setAvailableYears(res.data))
    .catch(err => console.error("Failed to fetch years", err));
  }, [token]);

  // --- Fetch current exam results ---
  useEffect(() => {
    if (!activeExam) return;
    const params = { examName: activeExam.name };
    if (selectedYear) params.academicYear = selectedYear;

    api.get("/api/students/results", {
      headers: { Authorization: `Bearer ${token}` },
      params
    })
    .then(res => setResults(res.data))
    .catch(err => console.error("Failed to fetch results", err));
  }, [activeExam, selectedYear, token]);

  // --- Fetch previous year results ---
  useEffect(() => {
    if (!selectedYear) return;
    api.get("/api/students/previous-year-results", {
      headers: { Authorization: `Bearer ${token}` },
      params: { academicYear: selectedYear }
    })
    .then(res => setPreviousYearResults(res.data))
    .catch(err => console.error("Failed to fetch previous year results", err));
  }, [selectedYear, token]);

  // --- Dummy subject-teacher mapping for notifications/queries ---
  const subjectsWithTeachers = [
    { subject: "Math", teacher: "Mr. Sharma" },
    { subject: "Science", teacher: "Ms. Verma" },
    { subject: "English", teacher: "Mrs. Iyer" },
    { subject: "History", teacher: "Mr. Gupta" },
    { subject: "Geography", teacher: "Ms. Singh" },
    { subject: "Computer", teacher: "Mr. Reddy" },
    { subject: "Physics", teacher: "Mr. Khan" },
    { subject: "Chemistry", teacher: "Ms. Das" },
  ];

  // --- Notify teacher ---
  const handleViewed = (subject) => {
    const teacher = subjectsWithTeachers.find(s => s.subject === subject)?.teacher || "";
    const exists = notifications.find(n => n.exam === activeExam?.name && n.subject === subject);
    if (!exists) {
      setNotifications([...notifications, { exam: activeExam?.name, subject, teacher }]);
      alert(`✅ ${teacher} notified for ${subject}`);
    }
  };

  // --- Send query ---
  const handleSendQuery = (subject) => {
    const message = queryMessages[subject];
    if (!message) return;
    const teacher = subjectsWithTeachers.find(s => s.subject === subject)?.teacher || "";
    setQueries([...queries, { exam: activeExam?.name, subject, teacher, message }]);
    setQueryMessages({ ...queryMessages, [subject]: "" });
    alert(`📨 Query sent to ${teacher}`);
  };

  // --- Helper to get results for a specific previous exam ---
  const getResultsByExam = (examName) => {
    return previousYearResults.filter(res => res.exam_name === examName);
  };

  return (
    <>
      <ParentPanel onToggle={setIsSidebarCollapsed} />

      <div className="dashboard-topbar">
        <span className="date-time">TODAY IS {new Date().toLocaleString()}</span>
        <span className="school-name">Semigon Learning</span>
      </div>

      <div className={`sr-main-content ${isSidebarCollapsed ? "collapsed" : ""}`}>
        {/* --- Current Exams --- */}
        <h3 className="sr-title">
          <FaBookOpen style={{ marginRight: "8px" }} /> Exams
        </h3>
        <div className="sr-card-grid">
          {exams.map((exam, index) => (
            <div
              key={index}
              className={`sr-card ${exam.color}`}
              onClick={() => setActiveExam(exam)}
            >
              <div className="sr-left">
                <div className="sr-label">{exam.name}</div>
                <div className="sr-count">See Results</div>
              </div>
              <div className="sr-icon-chip">{exam.icon}</div>
            </div>
          ))}
        </div>

        {/* --- Previous Year Selector --- */}
        <h3 className="sr-title" style={{ marginTop: "30px" }}>
          <FaBookOpen style={{ marginRight: "8px" }} /> Previous Year Results
        </h3>
        <div style={{ marginBottom: "20px" }}>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: "6px", fontSize: "14px" }}
          >
            <option value="">Select Year</option>
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {/* --- Previous Year Cards (NEW) --- */}
        {selectedYear && previousYearResults.length > 0 && (
          <div className="sr-card-grid">
            {[...new Set(previousYearResults.map(res => res.exam_name))].map((examName, idx) => (
              <div key={idx} className="sr-card sr-green">
                <div className="sr-left">
                  <div className="sr-label">{examName}</div>
                  <button
                    className="notify-btn"
                    style={{ marginTop: "8px" }}
                    onClick={() => setActivePrevExam({ exam_name: examName })}
                  >
                    See Results
                  </button>
                </div>
                <div className="sr-icon-chip">
                  <FaBookOpen />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* --- Progress Chart (static example) --- */}
        <h3 className="sr-title" style={{ marginTop: "30px" }}>
          <FaBookOpen style={{ marginRight: "8px" }} /> Progress Comparison
        </h3>
        <div
          style={{
            marginBottom: "30px",
            background: "#fff",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
            transition: "all 0.3s ease",
          }}
        >
          <ResponsiveContainer width="100%" height={360}>
            <AreaChart
              data={[ 
                { subject: "Math", "2022": 78, "2023": 85, "2024": 88 },
                { subject: "Science", "2022": 82, "2023": 90, "2024": 92 },
                { subject: "English", "2022": 80, "2023": 84, "2024": 85 },
                { subject: "History", "2022": 90, "2023": 70, "2024": 85 },
              ]}
              margin={{ top: 20, right: 40, left: 0, bottom: 5 }}
            >
              <defs>
                <linearGradient id="color2022" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#5a67d8" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="#5a67d8" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="color2023" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#48bb78" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="#48bb78" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="color2024" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ecc94b" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="#ecc94b" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="4 4" stroke="#e6e6e6" />
              <XAxis dataKey="subject" tick={{ fontSize: 14, fill: "#555" }} padding={{ left: 10, right: 10 }} />
              <YAxis tick={{ fontSize: 14, fill: "#555" }} />
              <Tooltip contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 3px 12px rgba(0,0,0,0.15)", fontSize: "14px" }} />
              <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ fontSize: 14 }} />
              <Area type="monotone" dataKey="2022" stroke="#5a67d8" fill="url(#color2022)" strokeWidth={3} activeDot={{ r: 6 }} />
              <Area type="monotone" dataKey="2023" stroke="#48bb78" fill="url(#color2023)" strokeWidth={3} activeDot={{ r: 6 }} />
              <Area type="monotone" dataKey="2024" stroke="#ecc94b" fill="url(#color2024)" strokeWidth={3} activeDot={{ r: 6 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* --- Current Exam Modal --- */}
      {activeExam && (
        <div className="sr-modal-backdrop" onClick={() => setActiveExam(null)}>
          <div className="sr-result-card" onClick={(e) => e.stopPropagation()}>
            <h3>{activeExam.name} - Results</h3>
            <table>
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Marks</th>
                  <th>Teacher</th>
                  <th>Notify</th>
                  <th>Query</th>
                </tr>
              </thead>
              <tbody>
                {results.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: "center" }}>No results found</td>
                  </tr>
                ) : (
                  results.map((res, idx) => {
                    const teacher = res.teacher_name || "";
                    return (
                      <tr key={idx}>
                        <td>{res.subject_name}</td>
                        <td>{res.marks}</td>
                        <td>{teacher}</td>
                        <td>
                          <button className="notify-btn" onClick={() => handleViewed(res.subject_name)}>Viewed</button>
                        </td>
                        <td>
                          <input
                            type="text"
                            placeholder="Type query..."
                            value={queryMessages[res.subject_name] || ""}
                            onChange={(e) => setQueryMessages({ ...queryMessages, [res.subject_name]: e.target.value })}
                            style={{ padding: "6px", borderRadius: "6px", width: "130px" }}
                          />
                          <button
                            className="notify-btn"
                            onClick={() => handleSendQuery(res.subject_name)}
                            style={{ marginLeft: "6px", padding: "6px 10px" }}
                          >
                            Send
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            <button className="download-btn">
              <FaDownload style={{ marginRight: "6px" }} /> Download PDF
            </button>
          </div>
        </div>
      )}

      {/* --- Previous Year Exam Modal (NEW) --- */}
      {activePrevExam && (
        <div className="sr-modal-backdrop" onClick={() => setActivePrevExam(null)}>
          <div className="sr-result-card" onClick={(e) => e.stopPropagation()}>
            <h3>{activePrevExam.exam_name} - Results</h3>
            <table>
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Marks</th>
                  <th>Teacher</th>
                </tr>
              </thead>
              <tbody>
                {getResultsByExam(activePrevExam.exam_name).map((res, idx) => (
                  <tr key={idx}>
                    <td>{res.subject_name}</td>
                    <td>{res.marks}</td>
                    <td>{res.teacher_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              className="download-btn"
              style={{ marginTop: "10px" }}
              onClick={() => alert("PDF download not implemented yet")}
            >
              <FaDownload style={{ marginRight: "6px" }} /> Download PDF
            </button>
          </div>
        </div>
      )}

      {/* --- Notifications Table --- */}
      <div className="table-container2" style={{ marginTop: "20px" }}>
        <table>
          <thead>
            <tr>
              <th>Exam</th>
              <th>Subject</th>
              <th>Teacher</th>
            </tr>
          </thead>
          <tbody>
            {notifications.length === 0 ? (
              <tr><td colSpan="3" style={{ textAlign: "center" }}>No notifications sent</td></tr>
            ) : (
              notifications.map((n, idx) => (
                <tr key={idx}>
                  <td>{n.exam}</td>
                  <td>{n.subject}</td>
                  <td>{n.teacher}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* --- Queries Table --- */}
      <div className="table-container2" style={{ marginTop: "20px" }}>
        <table>
          <thead>
            <tr>
              <th>Exam</th>
              <th>Subject</th>
              <th>Teacher</th>
              <th>Query</th>
            </tr>
          </thead>
          <tbody>
            {queries.length === 0 ? (
              <tr><td colSpan="4" style={{ textAlign: "center" }}>No queries sent yet</td></tr>
            ) : (
              queries.map((q, idx) => (
                <tr key={idx}>
                  <td>{q.exam}</td>
                  <td>{q.subject}</td>
                  <td>{q.teacher}</td>
                  <td>{q.message}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default SeeResults;
